import type {
  Agent,
  AuthConfig,
  AuthType,
  ExchangeCodeResponse,
  Organization,
  Profile,
  ViewMode,
} from "@superblocksteam/shared";
import {
  isPkceEnabled,
  SUPERBLOCKS_AUTHORIZATION_HEADER,
} from "@superblocksteam/shared";

import AuthProvider from "auth/auth0";
import TokenProvider from "auth/token";
import {
  FEDERATED_TOKEN_KEY,
  IDP_TOKEN_KEY,
  inspectIdpTokenInJwt,
  type IdpTokenInspection,
} from "core/auth/idp-token";
import { SUPERBLOCKS_UI_AGENT_BASE_URL } from "env";
import { AgentApiPaths } from "store/utils/agent";
import { OrchestratorApiPaths } from "store/utils/orchestrator";

import { callAgent, HttpMethod } from "../../utils/client";
import { callWithExpectedError } from "./callWithExpectedError";
//TODO(alex): uncomment once https://github.com/superblocksteam/types/pull/128 is merged
//import { ExchangeOauthCodeForTokenRequest } from "@superblocksteam/types"

const scopedAuthorizationHeaders = (scopedToken?: string) =>
  scopedToken
    ? { [SUPERBLOCKS_AUTHORIZATION_HEADER]: `Bearer ${scopedToken}` }
    : undefined;

type LegacyAgentAuthParams = {
  orchestrator: false;
  agents: Agent[];
  organization: Organization;
  body: any;
  profile?: Profile;
  mode?: ViewMode;
};

// for oauth-code
export async function exchangeCodeV3(
  params:
    | LegacyAgentAuthParams
    | {
        orchestrator: true;
        agents: Agent[];
        organization: Organization;
        profile?: Profile;
        accessCode?: string;
        codeVerifier?: string;
        integrationId: string;
        configurationId: string;
        authType: AuthType;
        authConfig: AuthConfig;
        enableDataPlaneGateway?: boolean;
        profileKey?: string;
        scopedToken?: string;
      },
) {
  if (params.orchestrator) {
    const {
      agents,
      organization,
      enableDataPlaneGateway,
      profileKey,
      scopedToken,
    } = params;
    return callWithExpectedError<void>({
      method: HttpMethod.Post,
      url: OrchestratorApiPaths.EXCHANGE_CODE,
      baseUrl: SUPERBLOCKS_UI_AGENT_BASE_URL,
      body: {
        accessCode: params.accessCode,
        codeVerifier: params.codeVerifier,
        profile: params.profile,
        authType: params.authType,
        authConfig: params.authConfig,
        integrationId: params.integrationId,
        configurationId: params.configurationId,
      },
      agents,
      organization,
      enableDataPlaneGateway,
      profileKey,
      headers: scopedAuthorizationHeaders(scopedToken),
    });
  } else {
    const { agents, organization, body, profile, mode } = params;
    // The legacy agent endpoint has no code_verifier parameter. Sending the
    // request without it would fail at the identity provider with a generic
    // invalid_grant, so stop here with a message that names the real cause.
    if (isPkceEnabled(body?.authConfig?.usePkce)) {
      throw new Error(
        "PKCE requires the orchestrator token exchange, which is unavailable for this integration.",
      );
    }
    const resp = await callAgent<ExchangeCodeResponse>({
      method: HttpMethod.Post,
      url: AgentApiPaths.EXCHANGE_CODE,
      agents,
      organization,
      body,
      query: {
        ...(profile?.key ? { profile: profile?.key } : {}),
        ...(profile?.id ? { profileId: profile?.id } : {}),
        ...(mode ? { mode } : {}),
      },
    });
    return {
      success: resp?.successful,
      error: resp?.error,
    };
  }
}

// the old userTokenLogout and logoutAgentCookies were identical, so we consolidate
export async function logoutApisV3(params: {
  orchestrator: boolean;
  agents: Agent[];
  organization: Organization;
  enableDataPlaneGateway?: boolean;
  profileKey?: string;
}) {
  if (params.orchestrator) {
    const { agents, organization, enableDataPlaneGateway, profileKey } = params;
    return callWithExpectedError<void>({
      method: HttpMethod.Post,
      url: OrchestratorApiPaths.AUTH_LOGOUT,
      baseUrl: SUPERBLOCKS_UI_AGENT_BASE_URL,
      body: {},
      agents,
      organization,
      enableDataPlaneGateway,
      profileKey,
    });
  } else {
    const { agents, organization } = params;
    const res = await callAgent<boolean>({
      method: HttpMethod.Post,
      agents,
      url: AgentApiPaths.AUTH_LOGOUT,
      organization,
    });
    return {
      success: typeof res === "boolean" && Boolean(res),
    };
  }
}

export function loginDatasourceV3(
  params:
    | {
        orchestrator: false;
        agents: Agent[];
        organization: Organization;
        body: any;
      }
    | {
        orchestrator: true;
        agents: Agent[];
        organization: Organization;
        integrationId: string;
        profile: Profile;
        token?: string;
        refreshToken?: string;
        idToken?: string;
        expiryTimestamp?: number;
        enableDataPlaneGateway?: boolean;
        profileKey?: string;
        scopedToken?: string;
      },
) {
  if (params.orchestrator) {
    // TODO: use agents and orchestrator if orgIsOnPremise(organization) === true
    const {
      // Discriminant only. Everything left in `body` is sent on the wire.
      orchestrator: _orchestrator,
      agents,
      organization,
      enableDataPlaneGateway,
      profileKey,
      scopedToken,
      ...body
    } = params;
    return callWithExpectedError<{ success: boolean }>({
      method: HttpMethod.Post,
      url: OrchestratorApiPaths.AUTH_LOGIN,
      baseUrl: SUPERBLOCKS_UI_AGENT_BASE_URL,
      body,
      agents,
      organization,
      enableDataPlaneGateway,
      profileKey,
      headers: scopedAuthorizationHeaders(scopedToken),
    });
  } else {
    const { agents, organization, body } = params;
    return callAgent<any>({
      method: HttpMethod.Post,
      agents,
      url: AgentApiPaths.AUTH_LOGIN,
      body: body,
      organization,
    });
  }
}

export async function checkAuthV3(params: {
  profile?: Profile;
  integrationId: string;
  agents: Agent[];
  organization: Organization;
  enableDataPlaneGateway?: boolean;
  profileKey?: string;
  scopedToken?: string;
}) {
  const {
    integrationId,
    profile,
    organization,
    agents,
    enableDataPlaneGateway,
    profileKey,
    scopedToken,
  } = params;
  const res = await callWithExpectedError<{
    authenticated: boolean;
  }>({
    method: HttpMethod.Post,
    url: OrchestratorApiPaths.CHECK_AUTH,
    baseUrl: SUPERBLOCKS_UI_AGENT_BASE_URL,
    body: {
      integrationId,
      profile,
    },
    organization,
    agents,
    enableDataPlaneGateway,
    profileKey,
    headers: scopedAuthorizationHeaders(scopedToken),
  });
  return {
    authenticated: res.success && res.result.authenticated,
  };
}

async function inspectIdpToken(): Promise<IdpTokenInspection> {
  // The Superblocks JWT first. Its `federated_token` is the one the server
  // re-minted out of the Token Vault, and the one the agent will use for the
  // exchange, so it is the only expiry worth blocking on. The Auth0 token's
  // claim is written once at login and never rewritten, not even by a silent
  // renewal, so gating on it turns every session over an hour old into a
  // re-authentication prompt no matter how healthy the vault is.
  const fromSuperblocksJwt = inspectIdpTokenInJwt(
    TokenProvider.getToken(),
    FEDERATED_TOKEN_KEY,
  );
  if (fromSuperblocksJwt.present) {
    return fromSuperblocksJwt;
  }

  // No federated token in the JWT: vault refresh is off for this org, or the
  // JWT predates it. Fall back to the login-time claim, which is what this
  // check read before the vault existed.
  try {
    const auth0Jwt = await AuthProvider.generateToken();
    return inspectIdpTokenInJwt(auth0Jwt, IDP_TOKEN_KEY);
  } catch {
    return { present: false };
  }
}

/**
 * Back-compat wrapper matching the old `{hasIdpJwt, isIdpJwtValid}` shape on
 * top of {@link inspectIdpToken}. Opaque tokens are reported as valid locally
 * so they are forwarded to the orchestrator, which validates them when
 * `agent.plugins.auth.validate_subject_token_during_obo_flow.enabled` is on.
 */
export async function checkIdpToken(): Promise<{
  hasIdpJwt: boolean;
  isIdpJwtValid?: boolean;
}> {
  const inspection = await inspectIdpToken();
  if (!inspection.present) {
    return { hasIdpJwt: false };
  }
  if (inspection.format === "opaque") {
    return { hasIdpJwt: true, isIdpJwtValid: true };
  }
  return { hasIdpJwt: true, isIdpJwtValid: !inspection.expired };
}
